import React, { useEffect } from 'react';
import BaseInput from '../Base/BaseInput';
import BaseSelect from '../Base/BaseSelect';
import { Passenger } from '../../models/PassengerModel';
import {
  finalizeFormTextField,
  sanitizeFormTextField,
} from '../../utils/textInputSanitize';
import './styles/PassengerForm.css';

type Props = {
  value: Passenger;
  onChange: (p: Passenger) => void;
  error?: string | null;
  companies: { value: string; label: string }[];
  fieldErrors?: Record<string, string>;
};

const PassengerForm: React.FC<Props> = ({ value, onChange, error, companies, fieldErrors = {} }) => {
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value: v } = e.target;
    onChange({ ...value, [name]: v });
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value: v } = e.target;
    onChange({ ...value, [name]: sanitizeFormTextField(name, v) });
  };

  const handleTextBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value: v } = e.target;
    const next = finalizeFormTextField(name, v);
    if (next !== (value as any)[name]) {
      onChange({ ...value, [name]: next });
    }
  };

  const handleRoleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    onChange({ ...value, [name]: checked });
  };

  const isCreate = !value.id;

  useEffect(() => {
    if (!value.addresses?.length) {
      onChange({ ...value, addresses: [{ street: '' }] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo reaccionar a cantidad de direcciones
  }, [value.addresses?.length]);

  const handleAddressChange = (index: number, val: string) => {
    const updated = [...(value.addresses || [])];
    updated[index] = { ...updated[index], street: sanitizeFormTextField('address', val) };
    onChange({ ...value, addresses: updated });
  };

  const handleAddressBlur = (index: number, val: string) => {
    const next = finalizeFormTextField('address', val);
    const updated = [...(value.addresses || [])];
    if (updated[index]?.street === next) return;
    updated[index] = { ...updated[index], street: next };
    onChange({ ...value, addresses: updated });
  };

  const addAddress = () =>
    onChange({
      ...value,
      addresses: [...(value.addresses || []), { id: 0, street: '' }],
    });

  const removeAddress = (index: number) => {
    const cur = value.addresses || [];
    if (cur.length <= 1) return;
    onChange({
      ...value,
      addresses: cur.filter((_, i) => i !== index),
    });
  };

  const addressRows = value.addresses?.length ? value.addresses : [{ street: '' }];
  const canRemoveAddress = addressRows.length > 1;

  return (
    <div className="passenger-form">
      <div className="form-row">
        <div className="form-field full-width">
          <BaseSelect
            label="Empresa"
            name="company"
            value={String(value.company ?? '')}
            onChange={handleSelectChange}
            options={companies.map((c) => ({ value: String(c.value), label: c.label }))}
            placeholder="Seleccionar empresa"
          />
          {fieldErrors.company && (
            <div className="form-error" role="alert">
              {fieldErrors.company}
            </div>
          )}
        </div>
      </div>

      <div className="form-row form-row--names">
        <div className="form-field">
          <BaseInput
            label="Nombre"
            name="name"
            value={value.name ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            required
            error={fieldErrors.name}
          />
        </div>

        <div className="form-field">
          <BaseInput
            label="Primer apellido"
            name="first_lastname"
            value={value.first_lastname ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            required
            error={fieldErrors.first_lastname}
          />
        </div>

        <div className="form-field">
          <BaseInput
            label="Segundo apellido"
            name="second_lastname"
            value={value.second_lastname ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            error={fieldErrors.second_lastname}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <BaseInput
            label="Celular"
            name="phone"
            value={value.phone ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            error={fieldErrors.phone}
          />
        </div>

        <div className="form-field">
          <BaseInput
            label="Email"
            name="email"
            type="email"
            value={value.email ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            error={fieldErrors.email}
          />
        </div>
      </div>

      {isCreate && (
        <div className="form-row">
          <div className="form-field full-width">
            <div className="passenger-roles" role="group" aria-label="Roles adicionales">
              <label className="passenger-roles__check">
                <input
                  type="checkbox"
                  name="also_as_requester"
                  checked={Boolean(value.also_as_requester)}
                  onChange={handleRoleCheckbox}
                />
                <span>Solicitante</span>
              </label>
              <label className="passenger-roles__check">
                <input
                  type="checkbox"
                  name="also_as_authorizer"
                  checked={Boolean(value.also_as_authorizer)}
                  onChange={handleRoleCheckbox}
                />
                <span>Autorizador</span>
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="addresses-section">
        <div className="addresses-header">
          <h4>Direcciones</h4>
          <button type="button" className="btn-add-address" onClick={addAddress}>
            <span className="btn-icon">+</span>
            Agregar dirección
          </button>
        </div>

        <div className="addresses-list">
          {addressRows.map((a, i) => (
            <div key={i} className="address-item">
              <div className="address-input-wrapper">
                <BaseInput
                  label={`Dirección ${i + 1}`}
                  name={`street-${i}`}
                  value={a.street ?? ''}
                  onChange={(e) => handleAddressChange(i, e.target.value)}
                  onBlur={(e) => handleAddressBlur(i, e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn-remove-address"
                onClick={() => removeAddress(i)}
                disabled={!canRemoveAddress}
                title={canRemoveAddress ? 'Eliminar dirección' : 'Debe quedar al menos una dirección'}
                aria-label={canRemoveAddress ? 'Eliminar dirección' : 'No se puede eliminar la única dirección'}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: 'block', margin: 'auto' }}
                >
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {fieldErrors.addresses && (
          <div className="form-error" role="alert">
            {fieldErrors.addresses}
          </div>
        )}
      </div>

      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default PassengerForm;
